import React, { useEffect, useRef, useState } from "react";
import { resetPassword, confirmResetPassword } from "@aws-amplify/auth";
import { Link, useLocation } from "react-router-dom";
import { configureAuth } from "../../auth/authConfig";
import { withSessionLock } from "../../auth/sessionQueue";
import { authorLoginReturnPath } from "../../services/groupInvitations";
import { getSafeReaderReturnTo } from "../../auth/readerReturn";
import PublicHeader from "./PublicHeader";
import { getPreferredAuthType } from "../../auth/authTypeStorage";

const PENDING_KEY = 'bookish-password-reset';
const inputClass = 'w-full border border-bookish-purple/15 rounded-lg px-3 py-2 min-h-[44px] bg-white focus:border-bookish-purple/40 focus:ring-2 focus:ring-bookish-purple/10 focus:outline-none';
const buttonClass = 'w-full inline-flex items-center justify-center bg-bookish-royal text-white py-3 min-h-[44px] rounded-lg hover:bg-bookish-purpleAccent font-semibold disabled:opacity-60';
function safeReturn(type, value) {
  return type === 'author' ? authorLoginReturnPath('?' + new URLSearchParams({ returnTo: value || '' })) : getSafeReaderReturnTo(value);
}
function initialContext(search) {
  let saved;
  try { saved = JSON.parse(sessionStorage.getItem(PENDING_KEY)); } catch { /* Storage may be unavailable. */ }
  const params = new URLSearchParams(search);
  const requested = params.get('type');
  const savedValid = saved && ['author', 'reader'].includes(saved.type) && typeof saved.email === 'string' && saved.email.trim();
  const type = ['author', 'reader'].includes(requested) ? requested : savedValid ? saved.type : getPreferredAuthType();
  const resume = savedValid && saved.type === type;
  const returnTo = safeReturn(type, params.has('returnTo') ? params.get('returnTo') : resume ? saved.returnTo : '');
  return { type, returnTo, email: resume ? saved.email.trim() : '', step: resume ? 'code' : 'email' };
}
function clearPending() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* Recovery still works without storage. */ }
}
function errorMessage(err) {
  const messages = {
    CodeMismatchException: 'That code doesn’t match. Enter the latest password-reset code from your email.',
    ExpiredCodeException: 'That code has expired. Select “Resend code” for a new one.',
    InvalidPasswordException: 'This password doesn’t meet your account’s requirements. Try a longer password with uppercase and lowercase letters, a number, and a symbol.',
    LimitExceededException: 'Too many attempts. Please wait a little before trying again.',
    TooManyRequestsException: 'Too many attempts. Please wait a little before trying again.',
    InvalidParameterException: 'We couldn’t complete this reset. Check your account type and email, or contact Lady Liza below for help.',
    NotAuthorizedException: 'We couldn’t complete this reset. Request a new code or return to login if you already changed your password.',
  };
  return messages[err?.name || err?.code] || 'We couldn’t complete that step. Please try again, or contact Lady Liza below for help.';
}

function ResetPasswordForm({ search }) {
  const [context] = useState(() => initialContext(search));
  const { type, returnTo } = context;
  const audience = type === 'author' ? 'Author' : 'Reader';
  const otherType = type === 'author' ? 'reader' : 'author';
  const loginPath = type === 'author' ? `/loginauthor?${new URLSearchParams({ returnTo })}` : returnTo ? `/loginreader?${new URLSearchParams({ returnTo })}` : '/loginreader';
  const [email, setEmail] = useState(context.email);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(context.step);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const heading = useRef(null);
  const lock = useRef(false);
  useEffect(() => { heading.current?.focus(); }, [step]);

  function remember() {
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ type, email: email.trim(), returnTo })); } catch { /* Never store the password or code. */ }
  }
  function success() {
    clearPending(); setCode(''); setNewPassword(''); setShowPassword(false); setMessage(''); setStep('success');
  }
  async function run(label, action) {
    if (lock.current) return;
    lock.current = true; setBusy(label); setError(''); setMessage('');
    try { await action(); }
    catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(''); }
  }
  async function sendCode() {
    await run(step === 'code' ? 'Sending a new reset code…' : 'Sending your reset code…', async () => {
      let result;
      try {
        result = await withSessionLock(() => { configureAuth(type); return resetPassword({ username: email.trim() }); });
      } catch (err) {
        // Use the same neutral response for an address that is not registered.
        if ((err?.name || err?.code) !== 'UserNotFoundException') throw err;
      }
      if (result?.nextStep?.resetPasswordStep === 'DONE') { success(); return; }
      remember(); setCode(''); setStep('code');
      setMessage('If this account can be recovered, a password-reset code has been sent. Use the most recent email.');
    });
  }
  async function submit(event) {
    event.preventDefault();
    if (lock.current || step === 'success' || !event.currentTarget.reportValidity()) return;
    if (step === 'email') { await sendCode(); return; }
    if (!code.trim() || !newPassword.trim()) { setError('Enter your password-reset code and a new password.'); return; }
    await run('Resetting your password…', async () => {
      await withSessionLock(() => { configureAuth(type); return confirmResetPassword({ username: email.trim(), confirmationCode: code.trim(), newPassword }); });
      success();
    });
  }
  function differentEmail() {
    clearPending(); setEmail(''); setCode(''); setNewPassword(''); setShowPassword(false); setError(''); setMessage(''); setStep('email');
  }

  return <div className="min-h-screen bg-bookish-cream text-bookish-royal"><PublicHeader audience={type} />
    <main className="account-page flex justify-center">
      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] w-full max-w-md border border-bookish-purple/8">
        <p className="text-sm uppercase tracking-widest mb-3">{audience} account</p>
        <h1 ref={heading} tabIndex={-1} className="text-3xl font-serif font-bold mb-4">{step === 'success' ? 'Your password is reset' : `Reset your ${audience} password`}</h1>
        {step === 'success' ? <>
          <p role="status" className="mb-6">You’re ready for your next chapter. Sign in to your {audience} account with your new password.</p>
          <Link to={loginPath} className={buttonClass}>Log in to your {audience} account</Link>
        </> : <>
          <p className="mb-5">Let’s get you back to your next chapter.</p>
          <form onSubmit={submit} aria-label="Password reset" aria-busy={Boolean(busy)}>
            <fieldset disabled={Boolean(busy)} className="space-y-4">
              {step === 'email' ? <div>
                <label className="account-field" htmlFor="reset-email">Email</label>
                <input id="reset-email" type="email" autoComplete="email" required className={inputClass} value={email} onChange={e => setEmail(e.target.value.trim())} />
              </div> : <>
                <p className="text-sm break-words">Use the <strong>password-reset code</strong> requested for <strong>{email}</strong>. This is separate from a signup verification code. Check your spam folder if it hasn’t arrived.</p>
                <div><label className="account-field" htmlFor="reset-code">Password-reset code</label><input id="reset-code" autoComplete="one-time-code" inputMode="numeric" required className={inputClass} value={code} onChange={e => setCode(e.target.value)} /></div>
                <div><label className="account-field" htmlFor="reset-password">New password</label><input id="reset-password" autoComplete="new-password" type={showPassword ? 'text' : 'password'} required aria-describedby="reset-password-help" className={inputClass} value={newPassword} onChange={e => setNewPassword(e.target.value)} /></div>
                <p id="reset-password-help" className="text-sm">Choose a unique password with uppercase and lowercase letters, a number, and a symbol. Avoid reusing a password from another account.</p>
                <label className="flex gap-2 items-center min-h-[44px]"><input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} />Show password</label>
              </>}
              <p role="alert" className="text-sm">{error}</p>
              <p role="status" className="text-sm">{busy || message}</p>
              <button type="submit" className={buttonClass}>{busy || (step === 'email' ? 'Send Reset Code' : 'Reset Password')}</button>
              {step === 'code' && <>
                <button type="button" className="block min-h-[44px] underline" onClick={sendCode}>Resend code</button>
                <button type="button" className="block min-h-[44px] underline" onClick={differentEmail}>Use a different email</button>
              </>}
            </fieldset>
          </form>
          <p className="mt-5 text-sm">Wrong account type? <Link className="underline" aria-disabled={Boolean(busy)} onClick={event => { if (lock.current) event.preventDefault(); else clearPending(); }} to={`/forgotpassword?type=${otherType}`}>Reset your {otherType === 'author' ? 'Author' : 'Reader'} password instead</Link></p>
          <Link to={loginPath} className="block py-3 underline">Back to {audience} Login</Link>
        </>}
        <p className="mt-5 text-sm">Need a hand? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
      </div>
    </main>
  </div>;
}
export default function ForgotPassword() {
  const location = useLocation();
  return <ResetPasswordForm key={location.search} search={location.search} />;
}
